import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { menuBarApi } from "../API/Api";
import { tokens } from "../../store/tokens";

// Must match HEADER_HEIGHT in Header.jsx (60 on phones, 72 from md up),
// otherwise this sticky bar leaves a gap or tucks under the header.
const HEADER_HEIGHT = { xs: 60, md: 72 };
const BAR_HEIGHT = 46;

const MenuTags = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [menuBarData, setMenuBarData] = useState([]);

  useEffect(() => {
    fetch(menuBarApi)
      .then((res) => res.json())
      .then((result) => setMenuBarData(result))
      .catch((error) => console.log("menuBarData Error => ", error));
  }, []);

  const isActive = (id) => location.pathname === `/explore/${id}`;

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);

  return (
    <Box
      component="nav"
      sx={{
        bgcolor: tokens.bg.inkSection,
        position: "sticky",
        top: HEADER_HEIGHT,
        zIndex: 1050,
        boxShadow: "0 4px 12px rgba(18,36,46,0.14)",
      }}
    >
      <Container sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Mobile: hamburger + dropdown (whole row is the tap target) */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Box
          onClick={handleOpenNavMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: BAR_HEIGHT,
            cursor: "pointer",
          }}
        >
          <IconButton
            size="small"
            aria-label="browse categories"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            sx={{ color: tokens.textOn.dark.primary, ml: -1, mr: 0.5 }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontFamily: tokens.font.body,
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: tokens.textOn.dark.secondary,
            }}
          >
            Browse categories
          </Typography>
        </Box>

          {/* Menu sits OUTSIDE the clickable row. React bubbles events from
              portals to their React parent, so tapping the backdrop to close
              used to reach the row's onClick and instantly reopen the menu. */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            PaperProps={{
              sx: {
                bgcolor: tokens.bg.inkSection,
                borderRadius: `${tokens.radius.sm}px`,
                mt: 1,
                minWidth: 200,
                // fit small phones and scroll long category lists
                maxWidth: "calc(100vw - 32px)",
                maxHeight: "60vh",
              },
            }}
          >
            {menuBarData?.map((element) => (
              <MenuItem
                key={element.id}
                selected={isActive(element.id)}
                onClick={() => {
                  handleCloseNavMenu();
                  navigate("/explore/" + element.id);
                }}
                sx={{
                  minHeight: 44, // comfortable touch target
                  borderLeft: `2px solid ${
                    isActive(element.id) ? tokens.color.teal : "transparent"
                  }`,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                  "&.Mui-selected": { bgcolor: "rgba(15,138,141,0.16)" },
                  "&.Mui-selected:hover": { bgcolor: "rgba(15,138,141,0.22)" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: tokens.font.body,
                    fontSize: "0.875rem",
                    fontWeight: isActive(element.id) ? 700 : 400,
                    whiteSpace: "normal",
                    color: isActive(element.id)
                      ? tokens.textOn.dark.brand
                      : tokens.textOn.dark.primary,
                  }}
                >
                  {element.title}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Tablet/desktop: inline category links, swipe/scroll if they overflow */}
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            display: { xs: "none", md: "flex" },
            minHeight: BAR_HEIGHT,
            width: "100%",
            minWidth: 0,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            // Centre when everything fits, but stay scrollable from the
            // first item when it doesn't. (justify-content: center would
            // clip the left edge of an overflowing row.)
            "& > :first-of-type": { ml: "auto" },
            "& > :last-of-type": { mr: "auto" },
          }}
        >
          {menuBarData?.map((element) => {
            const active = isActive(element.id);
            return (
              <Button
                key={element.id}
                onClick={() => navigate("/explore/" + element.id)}
                disableRipple
                sx={{
                  position: "relative",
                  flexShrink: 0,
                  px: { md: 1, lg: 1.5 },
                  minHeight: BAR_HEIGHT,
                  borderRadius: 0,
                  fontFamily: tokens.font.body,
                  fontSize: "0.8125rem",
                  fontWeight: active ? 700 : 500,
                  textTransform: "none",
                  color: active
                    ? tokens.textOn.dark.brand
                    : tokens.textOn.dark.secondary,
                  whiteSpace: "nowrap",
                  transition: "color 0.18s ease",
                  "&:hover": {
                    color: tokens.textOn.dark.brand,
                    bgcolor: "transparent",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 10,
                    right: 10,
                    bottom: 6,
                    height: 2,
                    borderRadius: `${tokens.radius.pill}px`,
                    backgroundColor: tokens.color.teal,
                    transform: active ? "scaleX(1)" : "scaleX(0)",
                    transition: "transform 0.2s ease",
                  },
                  "&:hover::after": { transform: "scaleX(1)" },
                }}
              >
                {element.title}
              </Button>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
};

export default MenuTags;